import { Fetch } from '@syncfusion/ej2-base';
import { DataManager, UrlAdaptor } from '@syncfusion/ej2-data';
import { TokenService } from '@services/token.service';

// Must match UserSecurity.ReissuedTokenHeader on the backend, and JwtInterceptor's copy of it.
const REISSUED_TOKEN_HEADER = 'X-Reissued-Token';

/**
 * A UrlAdaptor that carries our JWT, for the Syncfusion components that fetch their own data.
 *
 * DataManager issues its requests through ej2-base's `Fetch`, not Angular's HttpClient, so
 * `JwtInterceptor` never sees them and nothing attaches the bearer header. Under the old cookie
 * auth that went unnoticed - the browser sent the cookie on any request to our origin, whoever
 * made it - so every remote DataManager in the app silently lost its credentials the moment the
 * token replaced the cookie. Any `new DataManager({ url })` pointed at our API needs this adaptor
 * (or a subclass of it) rather than a bare `UrlAdaptor`.
 *
 * The token is read inside beforeSend rather than captured when the adaptor is constructed: the
 * server mints a replacement any time it changes our claims (switching organization, tank profile
 * or impersonation), and a DataManager typically outlives several of those - it is built once when
 * a component initialises and re-queried on every expand, filter and page. A header captured up
 * front would go stale at the first org switch, which is the failure the interceptor's
 * reissued-token handling exists to prevent.
 */
export class AuthUrlAdaptor extends UrlAdaptor {
  constructor(protected tokenService: TokenService) {
    super();
  }

  public beforeSend(dm: DataManager, request: Request, settings?: Fetch): void {
    super.beforeSend(dm, request, settings);

    const token = this.tokenService.getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    // The response side of the same contract. DataManager exposes the parsed body to
    // processResponse but never the Response itself, so the only place to read a response header is
    // Fetch's onLoad hook - which DataManager leaves unset, so claiming it here is safe. Same-origin
    // request, so no Access-Control-Expose-Headers is involved.
    if (settings) {
      settings.onLoad = (response: Response) => {
        const reissuedToken = response?.headers?.get(REISSUED_TOKEN_HEADER);
        if (reissuedToken) {
          this.tokenService.setTokenFromJwt(reissuedToken);
        }
      };
    }
  }
}
